import { entryDuration, entryEarnings, projectCurrency, type Range } from "./billing";
import { makeId } from "./clock";
import { MAX_INSTALLMENTS, type BillingDetails, type Discount, type FixedItemRef, type FixedLine, type Invoice, type InvoiceLine, type InvoiceSnapshot, type Project, type Task, type TaxLine, type TimeEntry } from "./entities";
import { addMoney, type Currency, type MoneyByCurrency } from "./money";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";
import { parentTask, taskLabel } from "./tasks";

const MINUTE = 60_000;
const HOUR = 3_600_000;

/** Local midnight of the day containing `ts`, shifted by `days`. */
function dayStart(ts: number, days = 0): number {
  const date = new Date(ts);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

/** USD first, then alphabetical: the order every per-currency list uses. */
export function sortCurrencies(currencies: Iterable<Currency>): Currency[] {
  return [...new Set(currencies)].sort((a, b) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
}

/** The billed days of an Invoice as a range: the last day is included. */
export function invoiceRange(invoice: Pick<Invoice, "periodFrom" | "periodTo">): Range {
  return { from: invoice.periodFrom, to: dayStart(invoice.periodTo, 1) };
}

export function liveInvoice(dataset: Dataset, id: string | null | undefined): Invoice | undefined {
  const invoice = id ? dataset.invoices[id] : undefined;
  return invoice && isLive(invoice) ? invoice : undefined;
}

/** The live Invoice an entry is on. Entries pointing at a deleted Draft count as uninvoiced. */
export function entryInvoice(dataset: Dataset, entry: TimeEntry): Invoice | undefined {
  return liveInvoice(dataset, entry.invoiceId);
}

/** Installments after the first: they bill a share of Installment 1's lines and hold no entries. */
function isFollower(invoice: Invoice): boolean {
  return invoice.installment !== null && invoice.installment.groupId !== invoice.id;
}

/** The live Installments of the split an Invoice belongs to, in order; empty when it is not split. */
export function installmentsOf(dataset: Dataset, invoice: Invoice): Invoice[] {
  const groupId = invoice.installment?.groupId;
  if (!groupId) return [];
  return Object.values(dataset.invoices)
    .filter((other) => isLive(other) && other.installment?.groupId === groupId)
    .sort((a, b) => a.installment!.index - b.installment!.index || a.createdAt - b.createdAt);
}

/**
 * The Invoice holding the entries, lines, period, discount, taxes, and Reference: Installment 1
 * of a split, otherwise the Invoice itself.
 */
export function installmentLead(dataset: Dataset, invoice: Invoice): Invoice {
  return isFollower(invoice) ? liveInvoice(dataset, invoice.installment!.groupId) ?? invoice : invoice;
}

/** How much of an Invoice's whole Sent or Paid Invoices bill, in basis points: all or nothing unless it is split. */
function sentShare(dataset: Dataset, lead: Invoice): number {
  const group = installmentsOf(dataset, lead);
  if (!group.length) return lead.status === "draft" ? 0 : 10_000;
  return Math.min(10_000, group.reduce((sum, invoice) => sum + (invoice.status === "draft" ? 0 : invoice.installment!.percent), 0));
}

/** Entries on a Sent or Paid Invoice are locked; entries a Draft has reserved stay editable. */
export function isEntryLocked(dataset: Dataset, entry: TimeEntry): boolean {
  const invoice = entryInvoice(dataset, entry);
  return invoice !== undefined && invoice.status !== "draft";
}

/** Finished, Billable time with a Rate Snapshot: the only time an Invoice bills by the hour. */
function billableByHour(entry: TimeEntry): boolean {
  return isLive(entry) && entry.end !== null && entry.billable && entry.rateSnapshot !== null;
}

function belongsTo(dataset: Dataset, entry: TimeEntry, clientId: string, currency: Currency): boolean {
  if (!entry.projectId || entry.rateSnapshot?.currency !== currency) return false;
  const project = dataset.projects[entry.projectId];
  return project !== undefined && isLive(project) && project.clientId === clientId;
}

const byStart = (a: TimeEntry, b: TimeEntry) => a.start - b.start || a.id.localeCompare(b.id);

/** Billable hourly entries for a Client and Currency that no live Invoice holds (other than `invoiceId`). */
export function invoiceableEntries(dataset: Dataset, clientId: string, currency: Currency, range?: Range, invoiceId?: string): TimeEntry[] {
  return Object.values(dataset.timeEntries).filter((entry) => {
    if (!billableByHour(entry) || !belongsTo(dataset, entry, clientId, currency)) return false;
    if (range && (entry.start < range.from || entry.start >= range.to)) return false;
    const holder = entryInvoice(dataset, entry);
    return !holder || holder.id === invoiceId;
  }).sort(byStart);
}

/** The entries an Invoice bills: reserved by it and still Billable for its Client and Currency. */
export function invoiceEntries(dataset: Dataset, invoice: Invoice): TimeEntry[] {
  return Object.values(dataset.timeEntries)
    .filter((entry) => entry.invoiceId === invoice.id && billableByHour(entry) && belongsTo(dataset, entry, invoice.clientId, invoice.currency))
    .sort(byStart);
}

/** Currencies a Client can be invoiced in: its billable Projects' plus any uninvoiced time's. */
export function clientCurrencies(dataset: Dataset, clientId: string): Currency[] {
  const found: Currency[] = [];
  for (const project of Object.values(dataset.projects)) {
    if (isLive(project) && project.clientId === clientId && project.billingMode !== "non-billable") found.push(projectCurrency(dataset, project));
  }
  for (const entry of Object.values(dataset.timeEntries)) {
    if (billableByHour(entry) && entry.projectId && dataset.projects[entry.projectId]?.clientId === clientId && !entryInvoice(dataset, entry)) found.push(entry.rateSnapshot!.currency);
  }
  return sortCurrencies(found);
}

/* ---------------------------------------------------------------- fixed price items */

export interface FixedItem {
  ref: FixedItemRef;
  project: Project;
  task: Task | null;
  name: string;
  price: number;
  currency: Currency;
  /** When the item became Earned: its Task done, or its Project completed. */
  earnedAt: number | null;
}

const sameItem = (a: FixedItemRef, b: FixedItemRef) => a.type === b.type && a.id === b.id;

export function fixedItem(dataset: Dataset, ref: FixedItemRef): FixedItem | undefined {
  if (ref.type === "project") {
    const project = dataset.projects[ref.id];
    if (!project || !isLive(project) || project.billingMode !== "fixed" || project.fixedPrice === null) return undefined;
    return { ref, project, task: null, name: project.name, price: project.fixedPrice, currency: projectCurrency(dataset, project), earnedAt: project.completedAt };
  }
  const task = dataset.tasks[ref.id];
  const project = task ? dataset.projects[task.projectId] : undefined;
  if (!task || !isLive(task) || task.fixedPrice === null || !project || !isLive(project) || project.billingMode !== "fixed") return undefined;
  return { ref, project, task, name: task.name, price: task.fixedPrice, currency: projectCurrency(dataset, project), earnedAt: task.doneAt };
}

/** Every Fixed Price item, optionally only a Client's in one Currency: Project prices first, then milestones. */
export function fixedItems(dataset: Dataset, filter?: { clientId: string; currency: Currency }): FixedItem[] {
  const items: FixedItem[] = [];
  const projects = Object.values(dataset.projects)
    .filter((project) => isLive(project) && project.billingMode === "fixed" && (!filter || (project.clientId === filter.clientId && projectCurrency(dataset, project) === filter.currency)))
    .sort((a, b) => a.name.localeCompare(b.name));
  for (const project of projects) {
    const own = fixedItem(dataset, { type: "project", id: project.id });
    if (own) items.push(own);
    const tasks = Object.values(dataset.tasks).filter((task) => task.projectId === project.id).sort((a, b) => a.name.localeCompare(b.name));
    for (const task of tasks) {
      const item = fixedItem(dataset, { type: "task", id: task.id });
      if (item) items.push(item);
    }
  }
  return items;
}

export const depositAmount = (price: number, percent: number) => Math.round(price * percent / 10_000);

/** What other live Invoices bill for an item: frozen amounts once Sent, Deposits while Draft. */
function billedElsewhere(dataset: Dataset, item: FixedItem, exceptId: string | null): number {
  let total = 0;
  for (const invoice of Object.values(dataset.invoices)) {
    // Later Installments repeat Installment 1's lines, so only Installment 1 counts.
    if (!isLive(invoice) || invoice.id === exceptId || isFollower(invoice)) continue;
    if (invoice.snapshot) {
      for (const line of invoice.snapshot.lines) if (line.item && sameItem(line.item, item.ref)) total += line.amount;
    } else {
      for (const line of invoice.fixedLines) if (line.kind === "deposit" && sameItem(line.item, item.ref)) total += depositAmount(item.price, line.percent);
    }
  }
  return total;
}

/** Whether another live Invoice already bills the item's balance, so this one cannot. */
function balanceElsewhere(dataset: Dataset, ref: FixedItemRef, exceptId: string | null): boolean {
  return Object.values(dataset.invoices).some((invoice) => isLive(invoice) && invoice.id !== exceptId && !isFollower(invoice) && (invoice.snapshot
    ? invoice.snapshot.lines.some((line) => line.item && sameItem(line.item, ref) && (line.kind === "balance" || line.kind === "fixed"))
    : invoice.fixedLines.some((line) => line.kind === "balance" && sameItem(line.item, ref))));
}

/** Money for an item on Sent or Paid Invoices. A split counts only the shares already Sent. */
export function fixedItemInvoiced(dataset: Dataset, ref: FixedItemRef): number {
  let total = 0;
  for (const invoice of Object.values(dataset.invoices)) {
    if (!isLive(invoice) || isFollower(invoice) || invoice.status === "draft" || !invoice.snapshot) continue;
    const share = sentShare(dataset, invoice);
    for (const line of invoice.snapshot.lines) if (line.item && sameItem(line.item, ref)) total += Math.round(line.amount * share / 10_000);
  }
  return total;
}

export interface FixedItemStatus {
  item: FixedItem;
  /** Billed by other Invoices, Sent or Draft. */
  elsewhere: number;
  /** This Invoice's lines for the item. */
  lines: FixedLine[];
  /** Price left after every Invoice, this one included. */
  remaining: number;
  /** Percentage of the price still free for a new Deposit, in basis points. */
  depositRoom: number;
  canAddBalance: boolean;
}

/** The Client's Fixed Price items as they stand for one Invoice, for its editor. */
export function fixedItemsForInvoice(dataset: Dataset, invoice: Invoice): FixedItemStatus[] {
  return fixedItems(dataset, { clientId: invoice.clientId, currency: invoice.currency }).map((item) => {
    const elsewhere = billedElsewhere(dataset, item, invoice.id);
    const lines = invoice.fixedLines.filter((line) => sameItem(line.item, item.ref));
    const deposits = lines.filter((line) => line.kind === "deposit").reduce((sum, line) => sum + depositAmount(item.price, line.percent), 0);
    const hasBalance = lines.some((line) => line.kind === "balance");
    const remaining = hasBalance ? 0 : Math.max(0, item.price - elsewhere - deposits);
    return {
      item,
      elsewhere,
      lines,
      remaining,
      depositRoom: item.price > 0 ? Math.floor(remaining / item.price * 10_000) : 0,
      canAddBalance: !hasBalance && remaining > 0 && item.earnedAt !== null && !balanceElsewhere(dataset, item.ref, invoice.id)
    };
  });
}

/* ---------------------------------------------------------------- lines and totals */

function rounded(ms: number, minutes: number | null): number {
  if (!minutes) return ms;
  const step = minutes * MINUTE;
  return Math.ceil(ms / step) * step;
}

interface HourGroup { line: InvoiceLine; ms: number; rate: number; start: number }

/** An Invoice's lines from the current data: grouped time, then Fixed Price items, then manual lines. */
export function computeInvoiceLines(dataset: Dataset, invoice: Invoice): InvoiceLine[] {
  const rounding = dataset.clients[invoice.clientId]?.roundingMinutes ?? null;
  const groups = new Map<string, HourGroup>();
  for (const entry of invoiceEntries(dataset, invoice)) {
    const project = dataset.projects[entry.projectId!]!;
    const task = entry.taskId ? dataset.tasks[entry.taskId] : undefined;
    const label = task ? taskLabel(dataset, task) : undefined;
    const rate = entry.rateSnapshot!.amount;
    let key: string, title: string | null, context: string | null, note: string | null, date: number | null = null;
    if (invoice.grouping === "entry") {
      key = `entry:${entry.id}`;
      title = entry.description || label || project.name;
      context = entry.description && label ? `${project.name}, ${label}` : title === project.name ? null : project.name;
      note = null;
      date = dayStart(entry.start);
    } else if (invoice.grouping === "task") {
      // Sub-task time bills under its parent, named in the notes.
      const parent = task ? parentTask(dataset, task) : undefined;
      const lineTask = parent ?? task;
      key = `task:${project.id}:${lineTask?.id ?? ""}:${rate}`;
      title = lineTask?.name ?? null;
      context = project.name;
      note = parent ? [task!.name, entry.description].filter(Boolean).join(": ") : entry.description || null;
    } else {
      key = `project:${project.id}:${rate}`;
      title = project.name;
      context = null;
      note = label ?? null;
    }
    const group = groups.get(key) ?? { line: { key, kind: "hours", title, context, date, notes: [], quantity: 0, unitPrice: rate, amount: 0, item: null, percent: null }, ms: 0, rate, start: entry.start };
    group.ms += rounded(entryDuration(entry, entry.end!), rounding);
    if (note && !group.line.notes.includes(note) && group.line.notes.length < 50) group.line.notes.push(note);
    groups.set(key, group);
  }
  const hours = [...groups.values()]
    .sort((a, b) => invoice.grouping === "entry"
      ? a.start - b.start
      : (a.line.context ?? a.line.title ?? "").localeCompare(b.line.context ?? b.line.title ?? "") || Number(a.line.title === null) - Number(b.line.title === null) || (a.line.title ?? "").localeCompare(b.line.title ?? "") || b.rate - a.rate)
    .map(({ line, ms, rate }) => ({ ...line, quantity: ms / HOUR, amount: Math.round(rate * ms / HOUR) }));

  const fixed: InvoiceLine[] = [];
  for (const line of invoice.fixedLines) {
    const item = fixedItem(dataset, line.item);
    if (!item) continue;
    const elsewhere = billedElsewhere(dataset, item, invoice.id);
    const deposits = invoice.fixedLines.filter((other) => other.kind === "deposit" && sameItem(other.item, line.item)).reduce((sum, other) => sum + depositAmount(item.price, other.percent), 0);
    const amount = line.kind === "deposit" ? depositAmount(item.price, line.percent) : Math.max(0, item.price - elsewhere - deposits);
    fixed.push({
      key: `${line.kind}:${line.id}`,
      kind: line.kind === "deposit" ? "deposit" : elsewhere > 0 || deposits > 0 ? "balance" : "fixed",
      title: item.name,
      context: item.task ? item.project.name : null,
      date: null,
      notes: [],
      quantity: 1,
      unitPrice: amount,
      amount,
      item: item.ref,
      percent: line.kind === "deposit" ? line.percent : null
    });
  }

  const manual: InvoiceLine[] = invoice.manualLines.map((line) => ({
    key: `manual:${line.id}`, kind: "manual", title: line.description, context: null, date: null, notes: [],
    quantity: line.quantity, unitPrice: line.unitPrice, amount: Math.round(line.quantity * line.unitPrice), item: null, percent: null
  }));
  return [...hours, ...fixed, ...manual];
}

export interface InvoiceTotals {
  subtotal: number;
  discount: number;
  /** Subtotal after discount: what taxes apply to, and what counts as Invoiced and Paid. */
  taxable: number;
  taxes: Array<TaxLine & { amount: number }>;
  total: number;
}

export function invoiceTotals(lines: readonly InvoiceLine[], discount: Discount | null, taxes: readonly TaxLine[]): InvoiceTotals {
  const subtotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const off = !discount ? 0 : discount.type === "percent" ? Math.round(subtotal * Math.min(discount.value, 10_000) / 10_000) : Math.min(discount.value, subtotal);
  const taxable = subtotal - off;
  const taxLines = taxes.map((tax) => ({ ...tax, amount: Math.round(taxable * tax.rate / 10_000) }));
  return { subtotal, discount: off, taxable, taxes: taxLines, total: taxable + taxLines.reduce((sum, tax) => sum + tax.amount, 0) };
}

/** One Installment's part of an amount. The last takes what rounding leaves, so the parts add up to the whole. */
function shareOf(amount: number, percents: readonly number[], position: number): number {
  const part = (percent: number) => Math.round(amount * percent / 10_000);
  if (position < percents.length - 1) return part(percents[position]!);
  return amount - percents.slice(0, -1).reduce((sum, percent) => sum + part(percent), 0);
}

/** An Installment's share of the whole Invoice's totals, at `position` among the split's shares. */
export function installmentTotals(whole: InvoiceTotals, percents: readonly number[], position: number): InvoiceTotals {
  const share = (amount: number) => shareOf(amount, percents, position);
  const subtotal = share(whole.subtotal);
  const discount = share(whole.discount);
  const taxes = whole.taxes.map((tax) => ({ ...tax, amount: share(tax.amount) }));
  const taxable = subtotal - discount;
  return { subtotal, discount, taxable, taxes, total: taxable + taxes.reduce((sum, tax) => sum + tax.amount, 0) };
}

export interface InstallmentPosition { index: number; count: number; percent: number }

export interface InvoiceDocument extends InvoiceSnapshot {
  /** What this Invoice bills: for an Installment, its share of the whole. */
  totals: InvoiceTotals;
  /** The whole Invoice before any split; the same as totals unless it is an Installment. */
  whole: InvoiceTotals;
  installment: InstallmentPosition | null;
  /** The period, Reference, and discount it prints: Installment 1's for a split, else its own. */
  shared: Pick<Invoice, "periodFrom" | "periodTo" | "reference" | "discount">;
}

/**
 * Both parties and the lines as of now: what a Draft shows and what sending freezes. Later
 * Installments take Installment 1's lines, frozen once it is Sent, with their own parties.
 */
function liveSnapshot(dataset: Dataset, invoice: Invoice): InvoiceSnapshot {
  const lead = installmentLead(dataset, invoice);
  const client = dataset.clients[invoice.clientId];
  const { paymentInstructions, ...issuer } = dataset.settings.businessProfile;
  const details: BillingDetails = client?.billingDetails ?? { name: "", address: "", email: "", taxId: "" };
  return {
    language: client?.invoiceLanguage ?? "en",
    issuer,
    recipient: { ...details, name: details.name || client?.name || "" },
    paymentInstructions: paymentInstructions[invoice.currency] || paymentInstructions["default"] || "",
    lines: lead.snapshot?.lines ?? computeInvoiceLines(dataset, lead)
  };
}

/** Everything the printed Invoice shows. Sent and Paid Invoices read their frozen copy. */
export function invoiceDocument(dataset: Dataset, invoice: Invoice): InvoiceDocument {
  const lead = installmentLead(dataset, invoice);
  const snapshot = invoice.snapshot ?? liveSnapshot(dataset, invoice);
  const whole = invoiceTotals(snapshot.lines, lead.discount, lead.taxes);
  const shared = { periodFrom: lead.periodFrom, periodTo: lead.periodTo, reference: lead.reference, discount: lead.discount };
  const group = installmentsOf(dataset, invoice);
  const position = group.findIndex((member) => member.id === invoice.id);
  if (position < 0) return { ...snapshot, totals: whole, whole, installment: null, shared };
  const percents = group.map((member) => member.installment!.percent);
  return { ...snapshot, totals: installmentTotals(whole, percents, position), whole, installment: { index: position + 1, count: group.length, percent: percents[position]! }, shared };
}

/* ---------------------------------------------------------------- drafts */

export interface DraftInput {
  clientId: string;
  currency: Currency;
  /** Any time on the first and last day billed. */
  periodFrom: number;
  periodTo: number;
}

/**
 * A new Draft for a Client: every uninvoiced Billable entry in the period, plus the balance of
 * every Fixed Price item Earned by the period's last day, whenever it was finished.
 */
export function createInvoiceDraft(dataset: Dataset, input: DraftInput, factory: OperationFactory, id = makeId("invoice")): SyncOperation[] {
  const client = dataset.clients[input.clientId];
  const range = { from: dayStart(input.periodFrom), to: dayStart(input.periodTo, 1) };
  const fixedLines: FixedLine[] = [];
  for (const item of fixedItems(dataset, { clientId: input.clientId, currency: input.currency })) {
    if (item.earnedAt === null || item.earnedAt >= range.to || balanceElsewhere(dataset, item.ref, null)) continue;
    if (item.price - billedElsewhere(dataset, item, null) > 0) fixedLines.push({ id: makeId("line"), item: item.ref, kind: "balance", percent: 0 });
  }
  return [
    factory.op("invoice", id, {
      clientId: input.clientId,
      currency: input.currency,
      status: "draft",
      periodFrom: range.from,
      periodTo: dayStart(input.periodTo),
      grouping: "task",
      paymentTermDays: client?.paymentTermDays ?? 15,
      taxes: (client?.defaultTaxes ?? []).map((tax) => ({ ...tax, id: makeId("tax") })),
      fixedLines
    }),
    ...invoiceableEntries(dataset, input.clientId, input.currency, range).map((entry) => factory.op("timeEntry", entry.id, { invoiceId: id }))
  ];
}

/** Moves a Draft's period: work outside it is released, uninvoiced work inside it is picked up. */
export function setInvoicePeriod(dataset: Dataset, invoice: Invoice, from: number, to: number, factory: OperationFactory): SyncOperation[] {
  const range = { from: dayStart(Math.min(from, to)), to: dayStart(Math.max(from, to), 1) };
  const period = { periodFrom: range.from, periodTo: dayStart(Math.max(from, to)) };
  // Installments share Installment 1's period; theirs is a copy for lists.
  const operations = [invoice, ...installmentsOf(dataset, invoice).filter((member) => member.id !== invoice.id)].map((member) => factory.op("invoice", member.id, period));
  for (const entry of Object.values(dataset.timeEntries)) {
    if (entry.invoiceId === invoice.id && (entry.start < range.from || entry.start >= range.to)) operations.push(factory.op("timeEntry", entry.id, { invoiceId: null }));
  }
  for (const entry of invoiceableEntries(dataset, invoice.clientId, invoice.currency, range)) operations.push(factory.op("timeEntry", entry.id, { invoiceId: invoice.id }));
  return operations;
}

/** Uninvoiced Billable work for the same Client and Currency from before a Draft's period. */
export function earlierUninvoiced(dataset: Dataset, invoice: Invoice): { entries: TimeEntry[]; ms: number; amount: number } {
  const entries = invoiceableEntries(dataset, invoice.clientId, invoice.currency, { from: 0, to: invoice.periodFrom });
  let ms = 0, amount = 0;
  for (const entry of entries) {
    ms += entryDuration(entry, entry.end!);
    amount += entryEarnings(entry, entry.end!)?.amount ?? 0;
  }
  return { entries, ms, amount };
}

/* ---------------------------------------------------------------- status */

export function nextInvoiceNumber(dataset: Dataset, year: number): { sequence: number; number: string } {
  let last = 0;
  for (const invoice of Object.values(dataset.invoices)) if (invoice.numberYear === year && invoice.sequence !== null) last = Math.max(last, invoice.sequence);
  const sequence = last + 1;
  return { sequence, number: `${dataset.settings.invoicePrefix}${year}-${String(sequence).padStart(3, "0")}` };
}

export function dueDateFor(issueDate: number, termDays: number): number {
  return dayStart(issueDate, termDays);
}

export interface SendChecks {
  /** Reasons the Invoice cannot be Sent yet. */
  blockers: string[];
  /** Gaps worth fixing that do not stop sending. */
  warnings: Array<{ message: string; fix: "profile" | "client" }>;
}

export function sendChecks(dataset: Dataset, invoice: Invoice): SendChecks {
  const document = invoiceDocument(dataset, invoice);
  const client = dataset.clients[invoice.clientId];
  const blockers: string[] = [];
  const warnings: SendChecks["warnings"] = [];
  const group = installmentsOf(dataset, invoice);
  const position = group.findIndex((member) => member.id === invoice.id);
  if (position > 0 && group[position - 1]!.status === "draft") blockers.push(`Send installment ${position} first. Installments go out in order.`);
  if (!document.lines.length) blockers.push("There is nothing to bill yet. Add time, a Fixed Price item, or a line.");
  if (!document.issuer.name.trim()) blockers.push("Your business name is empty. Add it in Settings so the invoice says who it is from.");
  if (document.lines.length && document.totals.total === 0) warnings.push({ message: "The total is zero.", fix: "client" });
  if (!document.recipient.address.trim()) warnings.push({ message: `${client?.name ?? "This client"} has no billing address.`, fix: "client" });
  if (!document.issuer.taxId.trim()) warnings.push({ message: "Your tax ID is empty.", fix: "profile" });
  if (!document.paymentInstructions.trim()) warnings.push({ message: `There are no payment instructions for ${invoice.currency}.`, fix: "profile" });
  return { blockers, warnings };
}

/**
 * Marks an Invoice Sent: it takes the next number (or keeps the one it already had), freezes its
 * lines and both parties, and locks its entries. Reserved entries it no longer bills are released.
 */
export function sendInvoice(dataset: Dataset, invoice: Invoice, issueDate: number, factory: OperationFactory): SyncOperation[] {
  const issue = dayStart(issueDate);
  const year = new Date(issue).getFullYear();
  const numbering = invoice.sequence !== null ? {} : (() => {
    const next = nextInvoiceNumber(dataset, year);
    return { number: next.number, numberYear: year, sequence: next.sequence };
  })();
  const billed = new Set(invoiceEntries(dataset, invoice).map((entry) => entry.id));
  const operations = [factory.op("invoice", invoice.id, {
    status: "sent", ...numbering, issueDate: issue, dueDate: dueDateFor(issue, invoice.paymentTermDays), paidAt: null, snapshot: liveSnapshot(dataset, invoice)
  })];
  for (const entry of Object.values(dataset.timeEntries)) {
    if (entry.invoiceId === invoice.id && !billed.has(entry.id)) operations.push(factory.op("timeEntry", entry.id, { invoiceId: null }));
  }
  return operations;
}

export function markInvoicePaid(invoice: Invoice, paidAt: number, factory: OperationFactory): SyncOperation[] {
  return [factory.op("invoice", invoice.id, { status: "paid", paidAt: dayStart(paidAt) })];
}

/**
 * Why a Sent Invoice cannot go back to Draft, or null. Paid ones are final, and Installments go
 * back newest first, so the ones still Sent always come before the Drafts.
 */
export function revertBlocker(dataset: Dataset, invoice: Invoice): string | null {
  if (invoice.status === "paid") return "Paid invoices are final.";
  if (invoice.status === "draft") return "This invoice is already a draft.";
  const group = installmentsOf(dataset, invoice);
  const position = group.findIndex((member) => member.id === invoice.id);
  const later = group.slice(position + 1).findLast((member) => member.status !== "draft");
  if (!later) return null;
  const label = `Installment ${group.indexOf(later) + 1}`;
  return later.status === "paid" ? `${label} is paid, so the installments before it stay sent.` : `${label} is sent too. Move it back to draft first.`;
}

/** A Sent Invoice back to Draft: its entries unlock and its lines follow the data again. It keeps its number. */
export function revertInvoiceToDraft(invoice: Invoice, factory: OperationFactory): SyncOperation[] {
  return [factory.op("invoice", invoice.id, { status: "draft", snapshot: null, dueDate: null, paidAt: null })];
}

/**
 * Sent Invoices and numbered Drafts are never deleted, so the numbering has no gaps. A split
 * Draft goes to Trash whole, from Installment 1, while none of its Installments has a number.
 */
export function invoiceDeletionBlocker(dataset: Dataset, invoice: Invoice): string | null {
  if (invoice.status !== "draft") return "Sent invoices can't be deleted.";
  if (invoice.number) return `This draft already has number ${invoice.number}. Deleting it would leave a gap in your numbering.`;
  const group = installmentsOf(dataset, invoice);
  if (isFollower(invoice)) return `This is installment ${group.findIndex((member) => member.id === invoice.id) + 1} of ${group.length}. Change the split to remove it.`;
  const numbered = group.find((member) => member.number !== null);
  if (numbered) return `Installment ${group.indexOf(numbered) + 1} already has number ${numbered.number}. Deleting the draft would leave a gap in your numbering.`;
  return null;
}

/** Moves a Draft to Trash. A split Draft goes as one Invoice: its other Installments are discarded. */
export function trashInvoice(dataset: Dataset, invoice: Invoice, now: number, factory: OperationFactory): SyncOperation[] {
  const others = installmentsOf(dataset, invoice).filter((member) => member.id !== invoice.id);
  return [
    factory.op("invoice", invoice.id, invoice.installment ? { deletedAt: now, installment: null } : { deletedAt: now }),
    ...others.map((member) => factory.op("invoice", member.id, { deletedAt: now, discardedAt: now }))
  ];
}

/* ---------------------------------------------------------------- installments */

/** Whole percentages, in basis points, spread as evenly as they go over `total`: 3 of 100% make 33, 33, 34. */
export function evenPercents(count: number, total = 10_000): number[] {
  const each = Math.floor(total / 100 / count) * 100;
  return Array.from({ length: count }, (_, index) => (index === count - 1 ? total - each * (count - 1) : each));
}

/**
 * Why an Invoice's split cannot become these shares (basis points, in order), or null. A single
 * share merges the split back into one Draft. Sent Installments keep their share, and numbered
 * ones are never removed, so the numbering has no gaps.
 */
export function installmentPlanError(dataset: Dataset, invoice: Invoice, percents: readonly number[]): string | null {
  const lead = installmentLead(dataset, invoice);
  const group = installmentsOf(dataset, lead);
  if (!group.length) {
    if (lead.status !== "draft") return "Only drafts can be split.";
    if (lead.number) return `This draft already has number ${lead.number}, so it can't be split. Split a new draft instead.`;
    if (percents.length < 2) return "Split it into at least 2 installments.";
  }
  if (percents.length < 1) return "Keep at least one installment.";
  if (percents.length > MAX_INSTALLMENTS) return `Split it into ${MAX_INSTALLMENTS} installments at most.`;
  if (percents.some((percent) => !Number.isInteger(percent) || percent < 100 || percent % 100 !== 0)) return "Each installment is a whole percentage, at least 1%.";
  const sum = percents.reduce((total, percent) => total + percent, 0);
  if (sum !== 10_000) return `The installments add up to ${sum / 100}%, not 100%.`;
  if (percents.length === 1 && lead.number) return `Installment 1 already has number ${lead.number}, so the installments can't be merged.`;
  for (const [position, member] of group.entries()) {
    const label = `Installment ${position + 1}`;
    if (position >= percents.length) {
      if (member.number) return `${label} already has number ${member.number}, so it can't be removed.`;
    } else if (member.status !== "draft" && member.installment!.percent !== percents[position]) {
      return `${label} is ${member.status}, so it stays at ${member.installment!.percent / 100}%.`;
    }
  }
  return null;
}

/**
 * Splits a Draft into Installments, changes a split's shares, or, given one share, merges it back.
 * Installment 1 stays the Draft it was, keeping its entries and lines. New Installments copy its
 * client, currency, period, terms, and note; removed ones are discarded. Check installmentPlanError first.
 */
export function setInstallments(dataset: Dataset, invoice: Invoice, percents: readonly number[], now: number, factory: OperationFactory): SyncOperation[] {
  const lead = installmentLead(dataset, invoice);
  const group = installmentsOf(dataset, lead);
  const members = group.length ? group : [lead];
  const operations: SyncOperation[] = [];
  if (percents.length === 1) operations.push(factory.op("invoice", lead.id, { installment: null }));
  else percents.forEach((percent, position) => {
    const installment = { groupId: lead.id, index: position + 1, percent };
    const member = members[position];
    if (!member) {
      operations.push(factory.op("invoice", makeId("invoice"), {
        clientId: lead.clientId, currency: lead.currency, status: "draft", periodFrom: lead.periodFrom, periodTo: lead.periodTo,
        grouping: lead.grouping, paymentTermDays: lead.paymentTermDays, note: lead.note, installment
      }));
    } else if (member.installment?.groupId !== lead.id || member.installment.index !== installment.index || member.installment.percent !== percent) {
      operations.push(factory.op("invoice", member.id, { installment }));
    }
  });
  for (const member of members.slice(percents.length)) operations.push(factory.op("invoice", member.id, { deletedAt: now, discardedAt: now }));
  return operations;
}

export function isOverdue(invoice: Invoice, now: number): boolean {
  return isLive(invoice) && invoice.status === "sent" && invoice.dueDate !== null && invoice.dueDate < dayStart(now);
}

/* ---------------------------------------------------------------- money */

function sumInvoices(dataset: Dataset, keep: (invoice: Invoice) => boolean, field: "taxable" | "total"): MoneyByCurrency {
  let totals: MoneyByCurrency = {};
  for (const invoice of Object.values(dataset.invoices)) {
    if (isLive(invoice) && keep(invoice)) totals = addMoney(totals, { amount: invoiceDocument(dataset, invoice).totals[field], currency: invoice.currency });
  }
  return totals;
}

const inRange = (ts: number | null, range: Range) => ts !== null && ts >= range.from && ts < range.to;

/** Invoiced: Sent or Paid Invoices by issue date, before tax so it compares with Earned. */
export function invoicedByCurrency(dataset: Dataset, range: Range): MoneyByCurrency {
  return sumInvoices(dataset, (invoice) => invoice.status !== "draft" && inRange(invoice.issueDate, range), "taxable");
}

/** Paid: Paid Invoices by paid date, before tax. */
export function paidByCurrency(dataset: Dataset, range: Range): MoneyByCurrency {
  return sumInvoices(dataset, (invoice) => invoice.status === "paid" && inRange(invoice.paidAt, range), "taxable");
}

/** What clients owe: Sent Invoices not yet Paid, tax included. */
export function outstandingByCurrency(dataset: Dataset, onlyOverdueAt?: number): MoneyByCurrency {
  return sumInvoices(dataset, (invoice) => invoice.status === "sent" && (onlyOverdueAt === undefined || isOverdue(invoice, onlyOverdueAt)), "total");
}

/** Earned money not yet on a Sent or Paid Invoice, optionally for one Client. */
export function uninvoicedByCurrency(dataset: Dataset, clientId?: string): MoneyByCurrency {
  let totals: MoneyByCurrency = {};
  // A split's entries stay partly uninvoiced until its last Installment is Sent.
  const shares = new Map<string, number>();
  const unsent = (invoice: Invoice | undefined) => {
    if (!invoice) return 10_000;
    if (!shares.has(invoice.id)) shares.set(invoice.id, 10_000 - sentShare(dataset, invoice));
    return shares.get(invoice.id)!;
  };
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!billableByHour(entry)) continue;
    if (clientId !== undefined && (!entry.projectId || dataset.projects[entry.projectId]?.clientId !== clientId)) continue;
    const left = unsent(entryInvoice(dataset, entry));
    const earned = left > 0 ? entryEarnings(entry, entry.end!) : null;
    if (earned) totals = addMoney(totals, left === 10_000 ? earned : { ...earned, amount: Math.round(earned.amount * left / 10_000) });
  }
  for (const item of fixedItems(dataset)) {
    if (item.earnedAt === null || (clientId !== undefined && item.project.clientId !== clientId)) continue;
    const left = item.price - fixedItemInvoiced(dataset, item.ref);
    if (left > 0) totals = addMoney(totals, { amount: left, currency: item.currency });
  }
  return totals;
}
